import { effect, inject, Injectable, signal, Injector, runInInjectionContext } from '@angular/core';
import { ProfileService } from '../profile/profile.service';
import { DepositService } from '../deposit/deposit.service';
import { DepositWatcherService } from '../deposit/deposit-watcher.service';
import { DepositCheckerService } from '../deposit/deposit-checker.service';
import { OperatorService } from '../operator/operator.service';
import { markBootup } from 'src/app/shared/utils/performance.utils';
import { AnalyticsService } from '../analytics/analytics.service';
import { BootupService } from '../bootup/bootup.service';
import { RoomsResultsService } from '../rooms-results/rooms-results.service';

@Injectable({
  providedIn: 'root',
})
export class InitService {
  private profileService = inject(ProfileService);
  private depositService = inject(DepositService);
  private depositChecker = inject(DepositCheckerService);
  private depositWatcher = inject(DepositWatcherService);
  private operatorService = inject(OperatorService);
  private roomsResultsService = inject(RoomsResultsService);
  private injector = inject(Injector);
  private analyticsService = inject(AnalyticsService);
  private bootupService = inject(BootupService);

  private initialisedSignal = signal<boolean | undefined>(undefined);
  private initServicePromises: Promise<unknown>[] = [];

  initialise() {
    runInInjectionContext(this.injector, () => {
      effect(
        async () => {
          const initialised = this.profileService.initialised();
          if (initialised) {
            const userId = this.profileService.userId;

            this.analyticsService.logBreadcrumb(`init.service: About to verify bootup`);
            await this.bootupService.verify();

            markBootup('bootup:init-start');

            if (!this.operatorService.isGameOnlyOperator()) {
              this.depositChecker.initialise(
                this.operatorService.operatorId,
                this.operatorService.operatorPlatform,
              );
              this.depositWatcher.initialise();
              this.depositService.initialise(userId);

              this.initServicePromises = [
                this.roomsResultsService.initialise(this.operatorService.gameId),
              ];
            }

            this.initialisedSignal.set(true);

            markBootup('bootup:init-end');
          }
        },
        { allowSignalWrites: true },
      );
    });
  }

  public async waitForServices(): Promise<void> {
    await Promise.all(this.initServicePromises);
  }

  readonly initialised = this.initialisedSignal.asReadonly();
}
